import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react"
import { translations, type TranslationKey } from "../i18n/translations"

export type Locale = "en" | "ru" | "pl"

interface LocaleContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: TranslationKey, ...args: string[]) => string
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "en",
  setLocale: () => {},
  t: (key, ...args) => {
    let s: string = translations[key]?.en ?? key
    args.forEach((arg, i) => {
      s = s.replace(`{${i}}`, arg)
    })
    s = s.replace("{n}", args[0] ?? "")
    return s
  },
})

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem("locale")
    if (saved === "en" || saved === "ru" || saved === "pl") return saved
    return "en"
  })

  useEffect(() => {
    localStorage.setItem("locale", locale)
  }, [locale])

  const setLocale = useCallback((l: Locale) => setLocaleState(l), [])

  const t = useCallback(
    (key: TranslationKey, ...args: string[]): string => {
      let s: string = translations[key]?.[locale] ?? translations[key]?.en ?? key
      args.forEach((arg, i) => {
        s = s.replace(`{${i}}`, arg)
      })
      s = s.replace("{n}", args[0] ?? "")
      return s
    },
    [locale],
  )

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
  )
}

export const useLocale = () => useContext(LocaleContext)
